import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Container,
  Fab,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import Alert from "@mui/material/Alert";
import LazyTable, {
  DEFAULT_ROWS_PER_PAGE,
  TableCellType,
  TableSortType,
} from "components/LazyTable";
import OpenLinkInNewTabContextMenuItem from "components/OpenInNewTabContextMenuItem";
import useGlobalContext from "hooks/globalData";
import useDebounce from "hooks/useDebounce";
import useListTitle from "hooks/useListTitle";
import { useState } from "react";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import { Link, useNavigate } from "react-router-dom";
import {
  BaseFilter,
  ListRequestOfBaseFilter,
  ListResponseOfWizard,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import persistanceStore from "stores/persistanceStore";

const WizardList = () => {
  const { formatMessage: t } = useIntl();
  const navigate = useNavigate();
  const {
    wizardService,
    wizardDetailsUrl,
    wizardCreateUrl,
  } = useGlobalContext();
  const { organizationId } = contextStore.useState();

  const [order, setOrder] = useState<TableSortType>("desc");
  const [orderBy, setOrderBy] = useState("name");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const searchTerm = persistanceStore.useState((s) => s.wizardFilterSearchTerm);
  const debouncedSearchTerm = useDebounce(searchTerm?.trim(), 500);

  useListTitle("wizards");

  const { isError, data, error, isFetching } = useQuery<ListResponseOfWizard>(
    [
      "wizards",
      organizationId,
      page,
      rowsPerPage,
      orderBy,
      order,
      debouncedSearchTerm,
    ],
    () =>
      wizardService.getWizards(
        ListRequestOfBaseFilter.fromJS({
          pageNumber: page,
          pageSize: rowsPerPage,
          sortField: orderBy,
          sortDescending: order === "desc",
          parameters: BaseFilter.fromJS({
            searchText: debouncedSearchTerm,
          }),
        })
      ),
    {
      keepPreviousData: true,
    }
  );

  const wizards = data?.data ?? [];

  const cells: Array<TableCellType> = [
    {
      accessor: "name",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "nameLabel" }),
    },
    {
      accessor: "organizationName",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "organization" }),
    },
  ];

  const onClick = (id: string) => {
    navigate(wizardDetailsUrl(id));
  };

  if (isError) {
    return (
      <Alert severity="error">
        <pre>{JSON.stringify(error, null, 2)}</pre>
      </Alert>
    );
  }

  return (
    <Container>
      <Box mb={3} textAlign="center">
        <Link to={wizardCreateUrl()} style={{ textDecoration: "none" }}>
          <Fab aria-label="add" color="primary" data-cy="addButton">
            <AddIcon />
          </Fab>
        </Link>
      </Box>

      <Box mb={3}>
        <Grid container spacing={2}>
          <Grid item sm={12} md={12}>
            <TextField
              fullWidth
              variant="outlined"
              value={searchTerm ?? ""}
              placeholder={t({ id: "searchPlaceholder" })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        persistanceStore.update((s) => {
                          s.wizardFilterSearchTerm = "";
                        })
                      }
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onChange={(e) =>
                persistanceStore.update((s) => {
                  s.wizardFilterSearchTerm = e.target.value;
                })
              }
              data-cy="searchText"
            />
          </Grid>
        </Grid>
      </Box>

      <LazyTable
        {...{
          rowCount: data?.totalRecords ?? 0,
          rows: wizards ?? [],
          loading: isFetching,
          dense: true,
          title: t({ id: "wizards" }),
          cells,
          page,
          setPage,
          order,
          setOrder,
          orderBy,
          setOrderBy,
          rowsPerPage,
          setRowsPerPage,
          onClick,
          contextMenuItems: [
            OpenLinkInNewTabContextMenuItem((id) => wizardDetailsUrl(id)),
          ],
        }}
      />
    </Container>
  );
};

export default WizardList;
